import { z } from "zod";

/**
 * The address a visitor picked in the host landing page's address bar, carried
 * through sign in and onboarding into the listing wizard's location step.
 *
 * `sessionStorage`, not the URL: an address is personal data and must never
 * sit in a query string, and the tab-scoped store survives the same-tab
 * redirects in between (the proxy's bounce to /auth, OTP, and the Google/Apple
 * OAuth round trip). It is cleared once the wizard saves the location step.
 *
 * Every read is defensive: storage throws in some private modes, and the value
 * can be stale or edited by hand. A missing or invalid value just means the
 * flow runs unpersonalized, as it did before the handoff existed.
 */

const STORAGE_KEY = "ezspot_host_address";

const hostAddressSchema = z.object({
  streetAddress: z.string().min(1),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  lat: z.number(),
  lng: z.number(),
});

export type HostAddress = z.infer<typeof hostAddressSchema>;

/** Parses a stored value; anything malformed reads as no address. */
export function parseHostAddress(raw: string | null): HostAddress | null {
  if (raw === null) return null;
  try {
    const result = hostAddressSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function readHostAddress(): HostAddress | null {
  if (typeof window === "undefined") return null;
  try {
    return parseHostAddress(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeHostAddress(address: HostAddress): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(address));
  } catch {
    // Storage unavailable: the host types the address again in the wizard.
  }
}

export function clearHostAddress(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}
